/**
 * Step 6: Summary & Preview
 * 
 * Final review of promo details:
 * - Basic Information summary
 * - Discount Configuration summary
 * - Targeting summary
 * - Eligibility & Conditions summary
 * - Usage Limits summary
 * - Stackability Rules summary
 * - Funding information
 * - Edit links to previous steps
 * - Save as Draft, Schedule, or Activate Now actions
 */

import React, { useState, useEffect } from 'react';
import { PromoFormData, PromoValidationErrors, PromoStatus, DiscountMode, BenefitType, BenefitLevel, EntityTargetingMode, PromoDomain, TransactionType, UserType, PromoVisibilityType } from '../../../../../api/promos/types';
import { getCampaigns } from '../../../../../api/promos';
import { getSellers, getSellerLocations } from '../../../../../api/shop';
import { Seller, SellerLocation } from '../../../../../api/shop/types/sellerTypes';
import { Campaign } from '../../../../../api/promos/types/campaignTypes';
import '../../../Campaigns/CreateCampaign/components/StepperButtons.css';
import '../../../Campaigns/CreateCampaign/components/SummaryCardStyles.css';
import './StepTargeting.css';

interface StepSummaryProps {
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  isSubmitting: boolean;
  onPrevious: () => void;
  onSaveDraft: () => Promise<void>;
  onActivate: () => Promise<void>;
  onDeactivate?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  onEditStep: (stepIndex: number) => void;
  isEditMode: boolean;
  hasChanges: boolean;
  originalStatus: PromoStatus | null;
}

const StepSummary: React.FC<StepSummaryProps> = ({
  formData,
  validationErrors,
  isSubmitting,
  onPrevious,
  onSaveDraft,
  onActivate,
  onDeactivate,
  onDelete,
  onEditStep,
  isEditMode = false,
  hasChanges = false,
  originalStatus = null,
}) => {
  // State for fetching related data
  const [campaignName, setCampaignName] = useState<string>('');
  const [sellerNames, setSellerNames] = useState<Record<string, string>>({});
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});
  const [loadingRelatedData, setLoadingRelatedData] = useState(true);

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Fetch campaign name
  useEffect(() => {
    const fetchCampaignName = async () => {
      if (!formData.campaignId) {
        setLoadingRelatedData(false);
        return;
      }
      try {
        const response = await getCampaigns({ pageNum: 1, pageSize: 100 });
        const campaign = response.campaigns.find(c => c.id === formData.campaignId);
        if (campaign) {
          setCampaignName(campaign.name);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setLoadingRelatedData(false);
      }
    };
    fetchCampaignName();
  }, [formData.campaignId]);

  // Fetch seller and location names
  useEffect(() => {
    const fetchEntityNames = async () => {
      if (!formData.scope?.entityTargeting?.entityIds || formData.scope.entityTargeting.entityIds.length === 0) {
        setLoadingRelatedData(false);
        return;
      }

      try {
        setLoadingRelatedData(true);
        const entityIds = formData.scope.entityTargeting.entityIds || [];
        
        // Fetch sellers
        const sellersResponse = await getSellers({ pageNum: 1, status: 'ACTIVE' });
        const sellersMap: Record<string, string> = {};
        sellersResponse.paginatedResults.forEach((seller: Seller) => {
          const sellerId = seller._id || seller.id;
          if (entityIds.includes(sellerId)) {
            sellersMap[sellerId] = seller.name;
          }
        });
        setSellerNames(sellersMap);

        // Fetch locations for selected sellers
        if (formData.scope.entityTargeting.subEntityIds && formData.scope.entityTargeting.subEntityIds.length > 0) {
          const subEntityIds = formData.scope.entityTargeting.subEntityIds;
          const locationsMap: Record<string, string> = {};
          
          // Fetch locations for each seller
          for (const sellerId of entityIds) {
            try {
              const locationsResponse = await getSellerLocations({
                seller: sellerId,
                status: 'ACTIVE',
                pageNum: 1,
              });
              locationsResponse.paginatedResults.forEach((location: SellerLocation) => {
                const locationId = location._id;
                if (locationId && subEntityIds.includes(locationId)) {
                  locationsMap[locationId] = location.name || location.locationCode || locationId;
                }
              });
            } catch (error) {
              // Error handled silently
            }
          }
          setLocationNames(locationsMap);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setLoadingRelatedData(false);
      }
    };

    fetchEntityNames();
  }, [formData.scope?.entityTargeting]);

  // Get domain display name
  const getDomainDisplayName = (domain: PromoDomain): string => {
    switch (domain) {
      case PromoDomain.ACADEMY:
        return 'Academy';
      case PromoDomain.SHOP:
        return 'Shop';
      case PromoDomain.BOOKING:
        return 'Booking';
      case PromoDomain.COACHING:
        return 'Coaching';
      default:
        return domain;
    }
  };

  // Get transaction type display name
  const getTransactionTypeDisplayName = (type: TransactionType): string => {
    switch (type) {
      case TransactionType.ACADEMY_ENROLLMENT:
        return 'Enrollment';
      case TransactionType.SHOP_PURCHASE:
        return 'Shop Purchase';
      case TransactionType.VENUE_BOOKING:
        return 'Venue Booking';
      case TransactionType.COACHING_ENROLLMENT:
        return 'Coaching Enrollment';
      default:
        return type;
    }
  };

  // Get user type display name
  const getUserTypeDisplayName = (userType: UserType): string => {
    switch (userType) {
      case UserType.ALL:
        return 'All Users';
      case UserType.NEW:
        return 'New Users Only';
      case UserType.RENEWAL:
        return 'Renewal Users';
      case UserType.REPEAT:
        return 'Repeat Users';
      default:
        return userType;
    }
  };

  // Get discount mode display name
  const getDiscountModeDisplayName = (mode: DiscountMode): string => {
    switch (mode) {
      case DiscountMode.AUTOMATIC:
        return 'Automatic';
      case DiscountMode.CODE_BASED:
        return 'Code Based';
      default:
        return mode || '-';
    }
  };

  // Get benefit level display name
  const getBenefitLevelDisplayName = (level: BenefitLevel): string => {
    switch (level) {
      case BenefitLevel.ITEM:
        return 'Item';
      case BenefitLevel.ORDER:
        return 'Order';
      default:
        return level;
    }
  };

  // Get benefit type display name
  const getBenefitTypeDisplayName = (type: BenefitType): string => {
    switch (type) {
      case BenefitType.PERCENTAGE_DISCOUNT:
        return 'Percentage';
      case BenefitType.FIXED_DISCOUNT:
      case BenefitType.FLAT_DISCOUNT:
        return 'Fixed Amount';
      case BenefitType.FREE_SHIPPING:
        return 'Free Shipping';
      default:
        return type;
    }
  };

  // Get targeting mode display name
  const getTargetingModeDisplayName = (): string => {
    if (!formData.scope?.entityTargeting) {
      return 'All';
    }
    if (formData.scope.entityTargeting.mode === EntityTargetingMode.INCLUDE) {
      return 'Specific Entities';
    }
    if (formData.scope.entityTargeting.mode === EntityTargetingMode.EXCLUDE) {
      return 'Exclude Entities';
    }
    return 'All';
  };

  // Get entity type label based on domain
  const getEntityTypeLabel = (): string => {
    switch (formData.domain) {
      case PromoDomain.SHOP:
        return 'Seller & Locations';
      case PromoDomain.ACADEMY:
        return 'Academy';
      case PromoDomain.BOOKING:
        return 'Venue';
      case PromoDomain.COACHING:
        return 'Coaching';
      default:
        return 'Entity';
    }
  };

  return (
    <div>
      {/* Step Header */}
      <div className="mb-4">
        <h5 className="mb-2">Summary & Preview</h5>
        <p className="text-muted mb-0">
          Review your promo details before saving or activating
        </p>
      </div>

      {/* Basic Information Summary */}
      <div className="summary-card basic-information">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Basic Information</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(0)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body three-column">
          <div className="summary-field">
            <label className="summary-field-label">Promo Name</label>
            <div className="summary-field-content">{formData.name}</div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Promo Code</label>
            <div className="summary-field-content">{formData.code || '-'}</div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Discount Mode</label>
            <div className="summary-field-content">
              {getDiscountModeDisplayName(formData.discountMode || DiscountMode.CODE_BASED)}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Transaction Type</label>
            <div className="summary-field-content">
              {getTransactionTypeDisplayName(formData.transactionType)}
            </div>
          </div>
          {formData.campaignId && (
            <div className="summary-field">
              <label className="summary-field-label">Link to Campaign</label>
              <div className="summary-field-content">
                {loadingRelatedData ? 'Loading...' : campaignName || '-'}
              </div>
            </div>
          )}
          <div className="summary-field">
            <label className="summary-field-label">Domain</label>
            <div className="summary-field-content">
              {getDomainDisplayName(formData.domain)}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Visibility</label>
            <div className="summary-field-content">
              {(formData.visibility?.type || PromoVisibilityType.PUBLIC) === PromoVisibilityType.ASSIGNED
                ? 'Assigned to Specific Users'
                : 'Public'}
            </div>
          </div>
        </div>
      </div>

      {/* Discount Configuration Summary */}
      <div className="summary-card">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Discount Configuration</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(1)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body three-column">
          <div className="summary-field">
            <label className="summary-field-label">Discount Level</label>
            <div className="summary-field-content">
              {formData.benefit?.level ? getBenefitLevelDisplayName(formData.benefit.level) : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Discount Type</label>
            <div className="summary-field-content">
              {formData.benefit?.type ? getBenefitTypeDisplayName(formData.benefit.type) : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Listing Title</label>
            <div className="summary-field-content">{formData.listingTitle || '-'}</div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Applied Title</label>
            <div className="summary-field-content">{formData.appliedTitle || '-'}</div>
          </div>
          {formData.benefit?.type === BenefitType.PERCENTAGE_DISCOUNT && (
            <>
              <div className="summary-field">
                <label className="summary-field-label">Percentage Value (%)</label>
                <div className="summary-field-content">
                  {(formData.benefit.value !== undefined && formData.benefit.value > 0) ? `${formData.benefit.value}%` : '-'}
                </div>
              </div>
              {formData.benefit.maxDiscountAmount && formData.benefit.maxDiscountAmount > 0 && (
                <div className="summary-field">
                  <label className="summary-field-label">Max Discount Cap (₹)</label>
                  <div className="summary-field-content">₹{formData.benefit.maxDiscountAmount}</div>
                </div>
              )}
              {formData.benefit.level === BenefitLevel.ITEM && formData.benefit.applicableCatalogueCategory && (
                <div className="summary-field">
                  <label className="summary-field-label">Category</label>
                  <div className="summary-field-content">{formData.benefit.applicableCatalogueCategory}</div>
                </div>
              )}
            </>
          )}
          {(formData.benefit?.type === BenefitType.FIXED_DISCOUNT || formData.benefit?.type === BenefitType.FLAT_DISCOUNT) && (
            <>
              <div className="summary-field">
                <label className="summary-field-label">Fixed Amount</label>
                <div className="summary-field-content">
                  {(formData.benefit.value !== undefined && formData.benefit.value > 0) ? `₹${formData.benefit.value}` : '-'}
                </div>
              </div>
              {formData.benefit.level === BenefitLevel.ITEM && formData.benefit.applicableCatalogueCategory && (
                <div className="summary-field">
                  <label className="summary-field-label">Category</label>
                  <div className="summary-field-content">{formData.benefit.applicableCatalogueCategory}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Targeting Summary */}
      <div className="summary-card">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Targeting</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(2)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body" style={{ gap: '16px' }}>
          {/* Two-column layout: Left - Promo applies to + Filters, Right - Entity names */}
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
            {/* Left Column: Promo applies to and Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: '0 0 auto', minWidth: '300px' }}>
              {/* Promo applies to label */}
              <label className="summary-field-label" style={{ width: 'auto', marginBottom: '0', fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>Promo applies to</label>
              
              {/* Specific Entities value */}
              <div className="summary-field-content" style={{ width: 'auto', fontWeight: 600, fontSize: '16px', color: '#1A1A1A' }}>
                {getTargetingModeDisplayName()}
              </div>

              {/* Refine what this promo applies to section - Filters */}
              {formData.scope?.filters && formData.scope.filters.length > 0 ? (
                <>
                  {/* Divider line */}
                  <div style={{ height: '1px', backgroundColor: '#E3E5E8', margin: '8px 0' }}></div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {formData.scope.filters.map((filter, index) => {
                      const getFieldDisplayName = (field: string): string => {
                        if (field === 'sport' || field === 'SPORT') return 'Sport';
                        if (field === 'catalogueCategory' || field === 'CATALOG_CATEGORY') return 'Catalogue Category';
                        if (field === 'serviceType' || field === 'SERVICE_TYPE') return 'Service Type';
                        return field;
                      };

                      const filterValues = Array.isArray(filter.value) ? filter.value : [filter.value];
                      
                      return (
                        <div 
                          key={index} 
                          className="targeting-filter-row"
                          style={{ 
                            marginTop: index > 0 ? '0' : '0',
                            padding: '16px',
                            borderRadius: '10px',
                            backgroundColor: '#F5FAFF',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}
                        >
                          <div className="targeting-filter-label" style={{ fontSize: '12px', fontWeight: 500, color: '#006EFF', marginBottom: '0' }}>
                            Filter {index + 1}
                          </div>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1' }}>
                              <label className="summary-field-label" style={{ width: 'auto', marginBottom: '0', fontSize: '13px', color: '#6B7280' }}>Field</label>
                              <div className="summary-field-content" style={{ width: 'auto', fontWeight: 500, fontSize: '16px', color: '#1A1A1A' }}>
                                {getFieldDisplayName(filter.field)}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1' }}>
                              <label className="summary-field-label" style={{ width: 'auto', marginBottom: '0', fontSize: '13px', color: '#6B7280' }}>Value</label>
                              <div className="summary-field-content" style={{ width: 'auto', fontWeight: 500, fontSize: '16px', color: '#1A1A1A', whiteSpace: 'normal', overflow: 'visible' }}>
                                {filterValues.filter(Boolean).join(', ')}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </div>
            
            {/* Right Column: Entity names */}
            {formData.scope?.entityTargeting?.entityIds && formData.scope.entityTargeting.entityIds.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: '1 1 auto' }}>
                <label className="summary-field-label" style={{ width: 'auto', marginBottom: '0', fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>
                  {getEntityTypeLabel()} to be {formData.scope.entityTargeting.mode === EntityTargetingMode.INCLUDE ? 'included' : 'excluded'}
                </label>
                <div className="summary-field-content" style={{ width: 'auto', fontWeight: 500, fontSize: '16px', color: '#1A1A1A', whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.5' }}>
                  {loadingRelatedData ? (
                    'Loading...'
                  ) : (
                    <>
                      {/* Display entity names */}
                      {formData.scope.entityTargeting.entityIds
                        .map((entityId) => sellerNames[entityId] || entityId)
                        .filter(Boolean)
                        .join(', ')}
                      {/* Display sub-entity names if available */}
                      {formData.scope.entityTargeting.subEntityIds && formData.scope.entityTargeting.subEntityIds.length > 0 && (
                        <>
                          {formData.scope.entityTargeting.entityIds.filter(Boolean).length > 0 && ', '}
                          {formData.scope.entityTargeting.subEntityIds
                            .map((subEntityId) => locationNames[subEntityId] || subEntityId)
                            .filter(Boolean)
                            .join(', ')}
                        </>
                      )}
                      {(!formData.scope.entityTargeting.entityIds.some(id => sellerNames[id]) && 
                        (!formData.scope.entityTargeting.subEntityIds || !formData.scope.entityTargeting.subEntityIds.some(id => locationNames[id]))) && 
                        '-'}
                    </>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Eligibility & Conditions Summary */}
      <div className="summary-card">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Eligibility & Conditions</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(3)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body three-column">
          <div className="summary-field">
            <label className="summary-field-label">User Eligibility</label>
            <div className="summary-field-content">
              {formData.eligibility?.userType ? getUserTypeDisplayName(formData.eligibility.userType) : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Minimum Order Value</label>
            <div className="summary-field-content">
              {formData.conditions?.minOrderAmount ? `₹${formData.conditions.minOrderAmount}` : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Minimum Quantity</label>
            <div className="summary-field-content">
              {formData.conditions?.minQuantity ? formData.conditions.minQuantity : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Minimum Duration (months)</label>
            <div className="summary-field-content">
              {formData.conditions?.minDurationMonths ? formData.conditions.minDurationMonths : '-'}
            </div>
          </div>
          {formData.conditions?.fullPaymentRequired && (
            <div className="summary-field full-width">
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <i className="ph-check-circle text-success" style={{ fontSize: '18px', color: '#16A34A' }}></i>
                <span className="summary-field-content">Require Full Payment</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage Limits Summary */}
      <div className="summary-card">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Usage Limits</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(3)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body three-column">
          <div className="summary-field">
            <label className="summary-field-label">Total Usage Limit</label>
            <div className="summary-field-content">
              {formData.usageLimits?.totalLimit ? formData.usageLimits.totalLimit : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Per User Limit</label>
            <div className="summary-field-content">
              {formData.usageLimits?.perUserLimit ? formData.usageLimits.perUserLimit : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Valid From Date</label>
            <div className="summary-field-content">
              {formData.validity?.startAt ? formatDate(formData.validity.startAt) : '-'}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Expiry Date</label>
            <div className="summary-field-content">
              {formData.validity?.endAt ? formatDate(formData.validity.endAt) : '-'}
            </div>
          </div>
          {formData.validity?.scheduleActivation && (
            <div className="summary-field full-width">
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <i className="ph-check-circle text-success" style={{ fontSize: '18px', color: '#16A34A' }}></i>
                <span className="summary-field-content">Schedule Activation</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stackability Rules Summary */}
      <div className="summary-card">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Stackability Rules</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(4)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body">
          {formData.stackingRules ? (
            <>
              {/* Always show all options, with checkmark for true, warning for false */}
              <div className="summary-field">
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  {formData.stackingRules.allowWithReferral ? (
                    <i className="ph-check-circle text-success" style={{ fontSize: '18px', color: '#16A34A' }}></i>
                  ) : (
                    <i className="ph-x-circle text-warning" style={{ fontSize: '18px', color: '#F59E0B' }}></i>
                  )}
                  <span className="summary-field-content">
                    Combine with Referral discount {!formData.stackingRules.allowWithReferral && '(Disabled)'}
                  </span>
                </div>
              </div>
              <div className="summary-field">
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  {formData.stackingRules.allowWithOtherPromos ? (
                    <i className="ph-check-circle text-success" style={{ fontSize: '18px', color: '#16A34A' }}></i>
                  ) : (
                    <i className="ph-x-circle text-warning" style={{ fontSize: '18px', color: '#F59E0B' }}></i>
                  )}
                  <span className="summary-field-content">
                    Allow with Other Promos {!formData.stackingRules.allowWithOtherPromos && '(Disabled)'}
                  </span>
                </div>
              </div>
              <div className="summary-field">
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  {formData.stackingRules.exclusive ? (
                    <i className="ph-check-circle text-success" style={{ fontSize: '18px', color: '#16A34A' }}></i>
                  ) : (
                    <i className="ph-x-circle text-warning" style={{ fontSize: '18px', color: '#F59E0B' }}></i>
                  )}
                  <span className="summary-field-content">
                    Exclusive discount {!formData.stackingRules.exclusive && '(Not Selected)'}
                  </span>
                </div>
              </div>
              <div className="summary-field">
                <label className="summary-field-label">Max Total Discount % Cap</label>
                <div className="summary-field-content">
                  {formData.stackingRules.maxTotalDiscountPercent ? `${formData.stackingRules.maxTotalDiscountPercent}%` : '-'}
                </div>
              </div>
            </>
          ) : (
            <div className="summary-field">
              <div className="summary-field-content">-</div>
            </div>
          )}
        </div>
      </div>

      {/* Funding Summary */}
      {formData.funding && (
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Funding</h6>
            <button
              type="button"
              className="summary-card-edit-button"
              onClick={() => onEditStep(4)}
            >
              <i className="ph-pencil"></i>
              Edit
            </button>
          </div>
          <div className="summary-card-body">
            <div className="summary-field">
              <label className="summary-field-label">Funded By</label>
              <div className="summary-field-content">
                {formData.funding.fundedBy === 'PLATFORM' ? 'Platform' : 'Shared'}
                {formData.funding.sharePercentage && formData.funding.fundedBy === 'SHARED' && (
                  <span> ({formData.funding.sharePercentage}%)</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons - Conditional rendering based on edit mode and changes */}
      <div className="stepper-buttons-container">
        {/* Save as Draft - Show only for create mode or edit mode with changes (not for DRAFT/ACTIVE detail pages) */}
        <div className="stepper-buttons-left">
          {(!isEditMode || (hasChanges && originalStatus !== PromoStatus.DRAFT && originalStatus !== PromoStatus.ACTIVE)) && (
            <button
              type="button"
              className="btn btn-link text-primary p-0 text-decoration-none"
              onClick={onSaveDraft}
              disabled={isSubmitting}
              style={{ 
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: '#006EFF'
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save as Draft'}
            </button>
          )}
        </div>
        
        <div className="stepper-buttons-right" style={{ gap: '12px', marginLeft: 'auto' }}>
          {/* Conditional button rendering based on edit mode, changes, and status */}
          {isEditMode && !hasChanges ? (
            // Edit mode + No changes: Check status to determine buttons
            originalStatus === PromoStatus.DRAFT ? (
              // DRAFT status + No changes: Show Activate and Delete buttons only
              <>
                <button
                  type="button"
                  className="stepper-activate-button"
                  onClick={onActivate}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Activating...
                    </>
                  ) : (
                    'Activate Now'
                  )}
                </button>
                <button
                  type="button"
                  className="stepper-activate-button"
                  onClick={() => {
                    if (onDelete) {
                      onDelete();
                    }
                  }}
                  disabled={isSubmitting}
                  style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
                >
                  Delete
                </button>
              </>
            ) : originalStatus === PromoStatus.ACTIVE ? (
              // ACTIVE status + No changes: Show Deactivate button only
              <button
                type="button"
                className="stepper-activate-button"
                onClick={async () => {
                  if (onDeactivate) {
                    await onDeactivate();
                  }
                }}
                disabled={isSubmitting}
                style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Deactivating...
                  </>
                ) : (
                  'Deactivate'
                )}
              </button>
            ) : (
              // INACTIVE and other statuses: Show Activate button only (no Delete)
              <button
                type="button"
                className="stepper-activate-button"
                onClick={onActivate}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Activating...
                  </>
                ) : (
                  'Activate Now'
                )}
              </button>
            )
          ) : (
            // Edit mode + Has changes OR Create mode: Show Update/Activate button
            <button
              type="button"
              className="stepper-activate-button"
              onClick={onActivate}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {isEditMode && hasChanges 
                    ? (originalStatus === PromoStatus.DRAFT ? 'Activating...' : 'Updating...')
                    : 'Activating...'}
                </>
              ) : (
                isEditMode && hasChanges
                  ? (originalStatus === PromoStatus.DRAFT ? 'Activate' : 'Update')
                  : 'Activate Now'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepSummary;
